import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import { HelpTip } from "../components/HelpTip";
import { Link } from "react-router-dom";
import { timeAgo } from "../lib/format";
import {
  AlertTriangle, CheckCircle2, Search, X, Loader2, Settings2,
} from "lucide-react";

const STATUS_CLASS = {
  mapped: "bg-black text-white",
  suggested: "bg-neutral-800 text-white",
  defaulted: "bg-neutral-500 text-white",
  unmatched: "bg-red-600 text-white",
};

export default function Mappings() {
  const [rows, setRows] = useState([]);
  const [senders, setSenders] = useState([]);
  const [defaultAcct, setDefaultAcct] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [senderFilter, setSenderFilter] = useState("all");
  const [drawerRow, setDrawerRow] = useState(null);
  const [defaultDrawerOpen, setDefaultDrawerOpen] = useState(false);
  const [taxDrawerOpen, setTaxDrawerOpen] = useState(false);
  const [tax, setTax] = useState(null);
  const loadTax = async () => {
    try { setTax((await api.get("/mappings/tax")).data); } catch (_e) { setTax(null); }
  };
  useEffect(() => { loadTax(); }, []);
  const [banner, setBanner] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const filterFromUrl = searchParams.get("status");

  useEffect(() => {
    if (filterFromUrl && filterFromUrl !== filter) setFilter(filterFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterFromUrl]);

  const load = useCallback(async () => {
    const params = {};
    if (filter && filter !== "all") params.status = filter;
    if (q) params.q = q;
    if (senderFilter && senderFilter !== "all") params.sender_id = senderFilter;
    const { data } = await api.get("/mappings", { params });
    setRows(data.items || []);
    setSenders(data.senders || []);
    setDefaultAcct(data.default || null);
  }, [filter, q, senderFilter]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { all: rows.length, mapped: 0, unmatched: 0, suggested: 0, defaulted: 0 };
    rows.forEach((r) => { c[r.mapping_status] = (c[r.mapping_status] || 0) + 1; });
    // Prompt 18 N2: the Tax row counts like any other mappable row.
    if (tax?.needs_tax_line) {
      c.all += 1;
      c[tax.account ? "mapped" : "unmatched"] += 1;
    }
    return c;
  }, [rows, tax]);
  const showTaxRow = tax?.needs_tax_line && !q && senderFilter === "all"
    && (filter === "all" || filter === (tax.account ? "mapped" : "unmatched"));

  const filtered = filter !== "all" || !!q || senderFilter !== "all";
  const clearFilters = () => {
    setFilter("all"); setQ(""); setSenderFilter("all");
    if (filterFromUrl) {
      const next = new URLSearchParams(searchParams);
      next.delete("status");
      setSearchParams(next, { replace: true });
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-8 py-10">
        <PageHeader
          eyebrow="company setup"
          title="Item → Account Mapping"
          description="Choose the GL account for each item your vendors bill you for. Mappings belong to your current Receiving GL."
          help="mapping-items"
        />

        {banner && (
          <div className={`mt-6 border p-4 flex items-start justify-between gap-4 ${
            banner.kind === "error" ? "border-red-600 text-red-600" : "border-black"}`}
               data-testid="mappings-banner">
            <div className="text-sm">{banner.text}</div>
            <button className="text-xs uppercase tracking-widest"
                    onClick={() => setBanner(null)}
                    data-testid="mappings-banner-dismiss">dismiss</button>
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2" data-testid="mappings-filters">
            {["all", "unmatched", "mapped", "suggested", "defaulted"].map((s) => (
              <button
                key={s}
                type="button"
                className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${
                  filter === s
                    ? "border-black bg-black text-white"
                    : "border-neutral-300 text-neutral-700"
                }`}
                onClick={() => setFilter(s)}
                data-testid={`filter-${s}`}
              >
                {s} · {counts[s] || 0}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={senderFilter}
              onChange={(e) => setSenderFilter(e.target.value)}
              className="text-xs uppercase tracking-widest border border-neutral-300 px-3 py-1.5 bg-white"
              data-testid="mappings-sender-filter"
              aria-label="filter by sending company"
            >
              <option value="all">all vendors</option>
              {senders.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} · {s.count}
                </option>
              ))}
            </select>
            <div className="flex items-center border border-neutral-300 px-3 py-1.5">
              <Search size={12} className="text-neutral-400 mr-2" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                placeholder="search item label"
                className="text-sm outline-none w-56"
                data-testid="mappings-search"
              />
            </div>
            <button
              type="button"
              className="btn-outline flex items-center gap-2"
              onClick={() => setDefaultDrawerOpen(true)}
              data-testid="open-default-drawer"
            >
              <Settings2 size={12} />
              default: {defaultAcct?.account_name || "unset"}
            </button>
          </div>
        </div>

        <div className="mt-6 border border-neutral-200" data-testid="mappings-table">
          <div className="grid grid-cols-12 text-xs uppercase tracking-widest bg-neutral-50 border-b border-neutral-200 px-4 py-3">
            <div className="col-span-4">Source item</div>
            <div className="col-span-2">Vendor</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 flex items-center">
              Target account
              <HelpTip text="Accounts come from your Receiving GL's chart of accounts. Header and total accounts aren't shown."
                       anchor="mapping-items" label="About target accounts" testid="help-target-account" />
            </div>
            <div className="col-span-1">Updated</div>
            <div className="col-span-1 text-right">Action</div>
          </div>
          {showTaxRow && (
            <div className="grid grid-cols-12 items-center px-4 py-3 border-b border-neutral-200 bg-amber-50/40 text-sm"
                 data-testid="tax-mapping-row">
              <div className="col-span-4">
                <div className="font-semibold">Tax</div>
                <div className="text-xs text-neutral-500">Sales tax paid on bills</div>
              </div>
              <div className="col-span-2 text-xs text-neutral-500">all vendors</div>
              <div className="col-span-2">
                <span className={`badge ${tax.account ? "badge-success" : "badge-warning"}`} data-testid="tax-mapping-status">
                  {tax.account ? "mapped" : "unmatched"}
                </span>
              </div>
              <div className="col-span-3 text-xs" data-testid="tax-mapping-account">
                {tax.account?.account_name || <span className="italic text-neutral-400">not set — invoices with tax hold</span>}
              </div>
              <div className="col-span-1 text-right">
                <button type="button" className="btn-outline text-xs" onClick={() => setTaxDrawerOpen(true)}
                        data-testid="tax-mapping-edit">
                  {tax.account ? "change" : "map"}
                </button>
              </div>
            </div>
          )}
          {rows.length === 0 && showTaxRow ? null : rows.length === 0 ? (
            filtered ? (
              <div className="px-4 py-16 text-center text-sm" data-testid="mappings-filtered-empty">
                <div className="text-neutral-700">No items match these filters.</div>
                <button type="button" className="btn-outline mt-4" onClick={clearFilters}
                        data-testid="mappings-clear-filters">Clear filters</button>
              </div>
            ) : (
              <div className="px-4 py-16 text-center text-sm" data-testid="mappings-empty">
                <div className="text-neutral-700">Items appear here when your first invoice arrives.</div>
                <Link to="/help#mapping-items" className="btn-outline inline-block mt-4"
                      data-testid="mappings-empty-cta">How mapping works</Link>
              </div>
            )
          ) : rows.map((r) => (
            <div key={r.source_item_key}
                 className="grid grid-cols-12 items-center px-4 py-3 border-b border-neutral-100 text-sm hover:bg-neutral-50"
                 data-testid={`mapping-row-${r.source_item_key}`}>
              <div className="col-span-4 font-mono truncate">
                <div className="text-neutral-900">{r.source_item_label || r.source_item_key}</div>
                <div className="text-xs text-neutral-400 truncate">{r.source_item_key}</div>
              </div>
              <div className="col-span-2 text-sm truncate" data-testid={`sender-${r.source_item_key}`}>
                {r.sender_display_name || <span className="text-neutral-400">—</span>}
              </div>
              <div className="col-span-2">
                <span className={`inline-block px-2 py-0.5 text-xs uppercase tracking-widest ${STATUS_CLASS[r.mapping_status] || "bg-neutral-300"}`}
                      data-testid={`status-${r.source_item_key}`}>
                  {r.mapping_status}
                </span>
                {r.target_erp && (
                  <span className="ml-1 inline-block text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-neutral-200 text-neutral-600 font-mono"
                        data-testid={`target-erp-${r.source_item_key}-${r.target_erp}`}>
                    {r.target_erp}
                  </span>
                )}
              </div>
              <div className="col-span-2 text-sm">
                {r.target_gl_account_name || r.target_gl_account_id || <span className="italic text-neutral-400">—</span>}
              </div>
              <div className="col-span-1 text-xs text-neutral-500">{timeAgo(r.updated_at)}</div>
              <div className="col-span-1 text-right">
                <button className="text-xs underline"
                        onClick={() => setDrawerRow(r)}
                        data-testid={`map-${r.source_item_key}`}>
                  {r.mapping_status === "mapped" ? "edit" : "map"}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 text-xs text-neutral-500">
          Unmatched items hold the push. Suggestions never post without your confirmation.
        </div>
      </div>

      {drawerRow && (
        <MapDrawer
          row={drawerRow}
          onClose={() => setDrawerRow(null)}
          onSaved={async (mapping) => {
            setDrawerRow(null);
            setBanner({ kind: "success",
                        text: `Mapped '${mapping.source_item_label}' → ${mapping.target_gl_account_name || mapping.target_gl_account_id}. Held invoices will retry on the next sync.` });
            await load();
          }}
        />
      )}
      {taxDrawerOpen && (
        <DefaultDrawer
          current={tax?.account}
          title="Tax account — sales tax paid on bills"
          prefix="tax"
          unsetText="unset — invoices with tax hold until this is set"
          saveFn={saveTaxAccount}
          onClose={() => setTaxDrawerOpen(false)}
          onSaved={async (a) => {
            setTaxDrawerOpen(false);
            setBanner({ kind: "success",
                        text: a ? `Tax account set to ${a.account_name}. Held invoices with tax will post on the next sync.`
                                : "Tax account cleared. Invoices with tax will hold." });
            await loadTax();
          }}
        />
      )}
      {defaultDrawerOpen && (
        <DefaultDrawer
          current={defaultAcct}
          onClose={() => setDefaultDrawerOpen(false)}
          onSaved={async (d) => {
            setDefaultDrawerOpen(false);
            setBanner({ kind: "success",
                        text: d ? `Default GL account set to ${d.account_name}. Unmatched items will post here on the next sync.`
                                : "Default cleared. Unmatched items will now hold the push." });
            await load();
          }}
        />
      )}
    </Layout>
  );
}

function useCoaSearch() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const search = useCallback(async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/mappings/coa", { params: q ? { q } : {} });
      setAccounts(data.accounts || []);
    } finally { setLoading(false); }
  }, []);
  return { accounts, loading, search };
}

function MapDrawer({ row, onClose, onSaved }) {
  const [selected, setSelected] = useState(null);
  const { accounts, loading, search } = useCoaSearch();
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { search(""); /* initial load */ }, [search]);

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.put("/mappings", {
        source_item_key: row.source_item_key,
        source_item_label: row.source_item_label,
        target_gl_account_id: selected.id,
        target_gl_account_name: selected.name,
        // target_erp omitted — the backend pins this to the company's
        // current Receiving GL so a Zoho save never clobbers the QBO row
        // for the same item (Prompt 12b).
        mapping_status: "mapped",
        suggestion_source: "user",
      });
      onSaved(data.mapping);
    } finally { setSaving(false); }
  };

  return (
    <DrawerShell title={`Map "${row.source_item_label}"`} onClose={onClose} testid="map-drawer">
      <div className="text-xs text-neutral-500 uppercase tracking-widest">Source key</div>
      <div className="text-sm font-mono mt-1">{row.source_item_key}</div>

      <div className="mt-6 text-xs text-neutral-500 uppercase tracking-widest">Search GL Expense accounts</div>
      <div className="flex items-center border border-neutral-300 px-3 py-2 mt-1">
        <Search size={12} className="text-neutral-400 mr-2" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); }}
          onKeyDown={(e) => { if (e.key === "Enter") search(q); }}
          onBlur={() => search(q)}
          placeholder="type to filter (press enter)"
          className="text-sm outline-none flex-1"
          data-testid="coa-search-input"
        />
      </div>
      <div className="border border-neutral-200 mt-2 max-h-72 overflow-y-auto" data-testid="coa-results">
        {loading ? (
          <div className="px-4 py-8 flex items-center justify-center text-sm text-neutral-500">
            <Loader2 size={14} className="animate-spin mr-2" /> loading accounts…
          </div>
        ) : accounts.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-neutral-500" data-testid="coa-empty">
            No connected GL, or no matching Expense accounts. Connect QuickBooks or Xero on the Connections page.
          </div>
        ) : accounts.map((a) => (
          <button key={a.id}
                  type="button"
                  onClick={() => setSelected(a)}
                  className={`w-full text-left px-4 py-2 border-b border-neutral-100 text-sm ${selected?.id === a.id ? "bg-black text-white" : "hover:bg-neutral-50"}`}
                  data-testid={`coa-option-${a.id}`}>
            <div className="flex justify-between">
              <div>{a.name}</div>
              <div className="text-xs opacity-70">{a.code || a.type}</div>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button type="button"
                className="btn-primary flex items-center gap-2"
                onClick={save}
                disabled={!selected || saving}
                data-testid="save-mapping">
          <CheckCircle2 size={12} /> {saving ? "saving…" : "save mapping"}
        </button>
        <button type="button" className="btn-outline" onClick={onClose} data-testid="cancel-mapping">
          cancel
        </button>
      </div>
    </DrawerShell>
  );
}

const saveDefault = async (selected) => {
  const { data } = await api.put("/mappings/default", selected
    ? { account_id: selected.id, account_name: selected.name, erp: null }
    : { account_id: null });
  return data.default;
};

export const saveTaxAccount = async (selected) => {
  const { data } = await api.put("/mappings/tax", selected
    ? { account_id: selected.id, account_name: selected.name }
    : { account_id: null });
  return data.account;
};

function DefaultDrawer({ current, onClose, onSaved, title = "Company default GL account",
                         saveFn = saveDefault, prefix = "default",
                         unsetText = "unset — unmatched items hold the push" }) {
  const [selected, setSelected] = useState(null);
  const { accounts, loading, search } = useCoaSearch();
  const [saving, setSaving] = useState(false);

  useEffect(() => { search(""); }, [search]);

  const save = async () => {
    setSaving(true);
    try {
      onSaved(await saveFn(selected));
    } finally { setSaving(false); }
  };

  return (
    <DrawerShell title={title} onClose={onClose} testid={`${prefix}-drawer`}>
      <div className="text-xs text-neutral-500 uppercase tracking-widest">Current</div>
      <div className="text-sm mt-1">
        {current?.account_name || <span className="italic text-neutral-400">{unsetText}</span>}
      </div>

      <div className="mt-6 text-xs text-neutral-500 uppercase tracking-widest">Pick a new account</div>
      <div className="border border-neutral-200 mt-2 max-h-72 overflow-y-auto">
        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-neutral-500">
            <Loader2 size={14} className="animate-spin inline mr-2" /> loading…
          </div>
        ) : accounts.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-neutral-500">
            No connected GL — connect an ERP first.
          </div>
        ) : accounts.map((a) => (
          <button key={a.id}
                  type="button"
                  onClick={() => setSelected(a)}
                  className={`w-full text-left px-4 py-2 border-b border-neutral-100 text-sm ${selected?.id === a.id ? "bg-black text-white" : "hover:bg-neutral-50"}`}
                  data-testid={`${prefix}-option-${a.id}`}>
            <div className="flex justify-between">
              <div>{a.name}</div>
              <div className="text-xs opacity-70">{a.code || a.type}</div>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button type="button" className="btn-primary" onClick={save} disabled={saving}
                data-testid={`save-${prefix}`}>
          {saving ? "saving…" : `save ${prefix}`}
        </button>
        <button type="button" className="btn-outline"
                onClick={async () => {
                  setSaving(true);
                  try {
                    onSaved(await saveFn(null));
                  } finally { setSaving(false); }
                }}
                data-testid={`clear-${prefix}`}>
          clear
        </button>
        <button type="button" className="btn-outline" onClick={onClose} data-testid={`cancel-${prefix}`}>
          cancel
        </button>
      </div>
    </DrawerShell>
  );
}

function DrawerShell({ title, onClose, testid, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex justify-end" onClick={onClose}
         data-testid={`${testid}-backdrop`}>
      <div className="w-[520px] max-w-full h-full bg-white border-l border-neutral-900 p-6 overflow-y-auto"
           onClick={(e) => e.stopPropagation()}
           data-testid={testid}>
        <div className="flex items-center justify-between mb-6">
          <div className="font-display text-xl font-semibold">{title}</div>
          <button type="button" onClick={onClose}
                  className="p-2 border border-neutral-200"
                  data-testid={`${testid}-close`}>
            <X size={14} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
